/**
 * Render one or more collapsible queue overview panels.
 */

import {IconNames} from '@wandb/weave/components/Icon';
import {ApolloCurrentQueryResult} from 'apollo-client';
import React, {useState} from 'react';

import {AllQueuesQuery} from '../../../generated/graphql';
import {SemanticLoader as Loader} from '../../utility/InstrumentedLoader';
import {useRunInfoMapFromQueues} from '../hooks';
import {DeleteQueueDialog} from './DeleteQueueDialog';
import {InfoBanner} from './InfoBanner';
import {QueuesTable} from './QueuesTable';
import {Queue} from './queueUtil';

type QueuesProps = {
  queues: Queue[];
  showEntityName: boolean;
  refetchQueues: () => Promise<ApolloCurrentQueryResult<AllQueuesQuery>>;
};

export const Queues = ({queues, refetchQueues}: QueuesProps) => {
  const [deletingQueue, setDeletingQueue] = useState<Queue | null>(null);
  const onQueueDelete = (queue: Queue) => {
    setDeletingQueue(queue);
  };
  const closeDialog = () => {
    setDeletingQueue(null);
  };
  const onQueueDeleted = async () => {
    await refetchQueues();
    setDeletingQueue(null);
  };

  const runInfoMap = useRunInfoMapFromQueues(queues);

  if (runInfoMap == null) {
    return <Loader active inline="centered" name="queues-loader" />;
  }

  return (
    <>
      <DeleteQueueDialog
        queue={deletingQueue}
        onClose={closeDialog}
        onDelete={onQueueDeleted}
      />
      <InfoBanner
        hintKey="queues-info-banner"
        icon={IconNames.Info}
        bannerContent="Queues let you organize and manage jobs waiting to run. Add jobs to a queue to launch them on the right resources with consistent configurations."
      />
      <>
        <QueuesTable
          queues={queues}
          runInfoMap={runInfoMap}
          onDeleteQueue={onQueueDelete}
        />
      </>
    </>
  );
};
